import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, BookOpen, BookOpenCheck, CalendarDays, ChevronRight, ClipboardCheck, Dumbbell, Flame, Footprints, HeartHandshake, Home, Layers, LayoutDashboard, LogOut, MessageSquare, Moon, Pencil, Plus, Salad, Search, Settings2, Sun, Trash2, TrendingUp, Trophy, UserRound, Users, Workflow } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Block, Faden, Kommentar } from '@/components/TrainingslogTab';
import CoachErnaehrungBereich, { istErnaehrung } from '@/pages/CoachErnaehrungBereich';
import CoachKundeAkte, { umleiten } from '@/pages/CoachKundeAkte';
import CoachingCheckins from '@/components/CoachingCheckins';
import CoachCheckins from '@/pages/CoachCheckins';
import Fortschrittsfotos from '@/components/Fortschrittsfotos';
import CoachChat from '@/components/CoachChat';
import Gewohnheiten from '@/components/Gewohnheiten';
import CoachTeamChallenges from '@/components/CoachTeamChallenges';
import TechnikVideos from '@/components/TechnikVideos';
import KiPlaner from '@/components/KiPlaner';
import Leistungstests from '@/components/Leistungstests';
import Uebungsbibliothek from '@/components/Uebungsbibliothek';
// Coach-Cockpit W2 (29.10.2026): Startseite Heute, Portfolio, Nachrichten, Kundeninfo neben dem Chat
import CoachHeute from '@/pages/CoachHeute';
import CoachPortfolio from '@/pages/CoachPortfolio';
import CoachNachrichten from '@/pages/CoachNachrichten';
import CoachKundeninfo from '@/components/CoachKundeninfo';
import TestimonialKarte from '@/components/TestimonialKarte';
import TestimonialBibliothek, { TestimonialSymbol } from '@/components/TestimonialBibliothek';
import { bibliothekCoach, testimonialCoach } from '@/lib/testimonialClient';
import { TrainingsplanEditor, PlanNeu, PersonenPlaene, Programme as PlanProgramme } from '@/components/PlanEditor2';
import CoachKalender from '@/pages/CoachKalender';
import CoachSuche from '@/components/CoachSuche';

/* Trainer-Webseite /coach (10.10.2026): Trainer ohne CRM-Konto sehen die Kraftlogs ihrer Athleten,
   korrigieren und tragen nach, kommentieren und passen Programme Woche für Woche an.
   Anmeldung per E-Mail-Code; Rechte prüft der Server (Overviewer alle, PT nur eigene Kunden).
   Eigene Anfragen mit eigenem Token — nicht die CRM-Anmeldung (lib/api leitet bei 401 aufs CRM-Login). */

const TOKEN = 'p1_coach_token';
const lies = () => { try { return localStorage.getItem(TOKEN); } catch { return null; } };
const merke = (t) => { try { t ? localStorage.setItem(TOKEN, t) : localStorage.removeItem(TOKEN); } catch { /* privat */ } };

const coach = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL || ''}/api/coach` });
coach.interceptors.request.use((cfg) => {
  const t = lies();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
// Übungsbibliothek (13.10.2026): feste Aufrufe, damit die Liste nicht bei jedem Zeichnen neu lädt
const bibLaden = () => coach.get('/bibliothek');
const bibSpeichern = (body) => coach.put('/bibliothek', body);
const kiStatus = () => coach.get('/ki-status');
const kiVorschlag = (b) => coach.post('/ki-vorschlag', b);
const ccLaden = () => coach.get('/coaching/checkins');
const tmBib = bibliothekCoach(coach, false);       // Testimonial-Bibliothek (W1.7): Contentplan-Idee nur als Master
const tmBibMaster = bibliothekCoach(coach, true);
const ccAntworten = (cid, text) => coach.post(`/coaching/checkins/${cid}/antwort`, { text });
const fehler = (e, rest) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : rest;
};

const datumDe = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');
const zeitDe = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const KRAFT = ['haupt', 'zubringer', 'power1', 'power2'];
const zahl = (v) => { const n = Number(String(v).replace(',', '.')); return Number.isFinite(n) ? n : NaN; };

/* ── Bausteine im Stil der Seite (Betreiber 12.10.2026: Muster „Stride") ── */

const kuerzel = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((t) => t[0].toUpperCase()).join('');
const THEMA = 'p1_coach_thema';
const liesThema = () => { try { return localStorage.getItem(THEMA) !== 'hell'; } catch { return true; } };

/* Die Seite hat ihr eigenes Thema (Standard dunkel), unabhängig vom CRM auf demselben Gerät. Die Klassen hängen am
   <html>, damit auch Dialoge und Auswahllisten außerhalb der Seite mitziehen; beim Verlassen ist alles wie vorher. */
function useCoachStil() {
  const [dunkel, setDunkel] = useState(liesThema);
  useEffect(() => {
    const root = document.documentElement;
    const vorher = root.classList.contains('dark');
    root.classList.add('coach-neu');
    return () => { root.classList.remove('coach-neu'); root.classList.toggle('dark', vorher); };
  }, []);
  useEffect(() => {
    // nach dem Thema-Effekt des CRM (ThemeProvider läuft beim ersten Laden nach dieser Seite)
    const id = requestAnimationFrame(() => document.documentElement.classList.toggle('dark', dunkel));
    try { localStorage.setItem(THEMA, dunkel ? 'dunkel' : 'hell'); } catch { /* privat */ }
    return () => cancelAnimationFrame(id);
  }, [dunkel]);
  return [dunkel, setDunkel];
}

/* Zähler für die Seitenleiste (15.10.2026): aus der vorgerechneten Cockpit-Liste (`/x/ernaehrung/cockpit`, Spiegel mit
   Trainer-Token) — ungelesene Nachrichten plus neue Check-ins fürs Cockpit, Kunden mit Handlungsbedarf für „Kunden“.
   Nur mit Ernährungs-Zugang; alle zwei Minuten neu; scheitert lautlos (dann kein Zähler). */
function useCockpitZaehler(an) {
  const [z, setZ] = useState(null);
  useEffect(() => {
    if (!an) { setZ(null); return undefined; }
    let weg = false;
    const lade = () => coach.get('/x/ernaehrung/cockpit').then((r) => {
      if (weg) return;
      const k = r.data?.kunden || [];
      setZ({
        cockpit: k.reduce((n, x) => n + (x.ungelesen?.summe || 0) + (x.checkin?.neu ? 1 : 0), 0),
        kunden: k.filter((x) => x.checkin?.schlecht || x.risiko?.ampel === 'rot').length,
      });
    }).catch(() => { /* ältere Routen oder kein Zugriff: kein Zähler */ });
    lade();
    const t = setInterval(lade, 120000);
    return () => { weg = true; clearInterval(t); };
  }, [an]);
  return z;
}

/* Zähler Check-in-Inbox und ungelesene Nachrichten (Integration W2, 29.10.2026): EIN leichter Aufruf `GET /zaehler` —
   dieselben Zählungen wie die Inbox und die Nachrichten-Seite. Neu beim Bereichswechsel und jede Minute; lautlos. */
function useInboxZaehler(an, ansicht) {
  const [z, setZ] = useState(null);
  useEffect(() => {
    if (!an) { setZ(null); return undefined; }
    let weg = false;
    const lade = () => coach.get('/zaehler').then((r) => { if (!weg) setZ(r.data); }).catch(() => { /* kein Zähler */ });
    lade();
    const t = setInterval(lade, 60000);
    return () => { weg = true; clearInterval(t); };
  }, [an, ansicht]);
  return z;
}

function Kachel({ label, wert, einheit, sub, icon: Icon, verzug = 0 }) {
  return (
    <div className="coach-rein rounded-[14px] border border-border/70 bg-card p-5" style={{ animationDelay: `${verzug}ms`, boxShadow: 'var(--schatten)' }}>
      <div className="flex items-center justify-between text-sm font-semibold text-muted-foreground">
        <span>{label}</span>{Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
      </div>
      <p className="mt-3 text-3xl font-extrabold leading-none tabular-nums">{wert}{einheit && <span className="ml-1.5 text-sm font-semibold text-muted-foreground">{einheit}</span>}</p>
      {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Balken({ label, hinweis, wert, anteil }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 break-words font-semibold">{label}{hinweis && <span className="ml-1.5 text-xs font-medium text-muted-foreground">{hinweis}</span>}</span>
        <span className="shrink-0 font-extrabold tabular-nums">{wert}</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="coach-balken h-full rounded-full bg-marke" style={{ width: `${Math.max(0, Math.min(100, anteil))}%` }} />
      </div>
    </div>
  );
}

const Abschnitt = ({ titel, sub, rechts, children, className = '' }) => (
  <section className={`p1-karte border bg-card p-5 sm:p-6 ${className}`}>
    <div className="mb-4 flex items-start justify-between gap-4">
      <div><h3 className="text-base font-extrabold">{titel}</h3>{sub && <p className="text-sm text-muted-foreground">{sub}</p>}</div>
      {rechts}
    </div>
    {children}
  </section>
);

/* ── Anmeldung ── */

function Anmeldung({ onDrin }) {
  // Standard: Zugangsdaten der P1-App (E-Mail + PIN, App-Rolle Trainer/Leitung). Alternativ Code per E-Mail
  // für Trainer ohne App-Konto (z. B. externe Personal Trainer, im CRM angelegt).
  const [weg, setWeg] = useState('app');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [code, setCode] = useState('');
  const [schritt, setSchritt] = useState(1);
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState('');

  const mitApp = async (e) => {
    e.preventDefault(); setBusy(true); setMeldung('');
    try { const r = await coach.post('/anmelden/app', { email, pin }); merke(r.data.token); onDrin(r.data.trainer); }
    catch (x) { setMeldung(fehler(x, 'Anmeldung fehlgeschlagen')); }
    setBusy(false);
  };
  const anfordern = async (e) => {
    e.preventDefault(); setBusy(true); setMeldung('');
    try { await coach.post('/anmelden/code', { email }); setSchritt(2); }
    catch (x) { setMeldung(fehler(x, 'Gerade nicht erreichbar. Bitte später noch einmal.')); }
    setBusy(false);
  };
  const bestaetigen = async (e) => {
    e.preventDefault(); setBusy(true); setMeldung('');
    try { const r = await coach.post('/anmelden/bestaetigen', { email, code }); merke(r.data.token); onDrin(r.data.trainer); }
    catch (x) { setMeldung(fehler(x, 'Anmeldung fehlgeschlagen')); }
    setBusy(false);
  };
  const wechsel = (w) => { setWeg(w); setSchritt(1); setMeldung(''); setPin(''); setCode(''); };
  const emailFeld = (
    <div className="space-y-1.5">
      <Label htmlFor="coach-email">E-Mail-Adresse</Label>
      <Input id="coach-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        className="h-11" placeholder="name@beispiel.de" data-testid="coach-email" />
    </div>
  );
  const reiter = (w, text, tid) => (
    <button type="button" role="tab" aria-selected={weg === w} onClick={() => wechsel(w)} data-testid={tid}
      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${weg === w ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
      {text}
    </button>
  );

  return (
    <main className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Markenfläche (ab Desktop) */}
      {/* „dark“ setzt die CRM-Farbvariablen dieses Abschnitts aufs dunkle Thema — dunkel auch im hellen Modus */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-border bg-card p-12 text-foreground">
        <div aria-hidden="true" className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-marke/25 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-marke/15 blur-3xl" />
        <img src="/logo-weiss-kompakt.png" alt="P1 Performance Center" className="relative hidden h-16 w-auto self-start object-contain dark:block" />
        <img src="/logo-black-kompakt.png" alt="P1 Performance Center" className="relative h-16 w-auto self-start object-contain dark:hidden" />
        <div className="relative space-y-6 max-w-md">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Trainer-Bereich</p>
            <h1 className="text-5xl font-extrabold tracking-tight">Coaching</h1>
            <p className="text-base text-muted-foreground">Sieh, was deine Athleten und Kunden im PT und im Kurs machen.</p>
          </div>
          <ul className="space-y-3 text-sm">
            {[[Users, 'Alle Einheiten deiner Athleten — Plan gegen Ist'], [Pencil, 'Sätze korrigieren, nachtragen, kommentieren'], [Layers, 'Programme Woche für Woche anpassen']].map(([Icon, text]) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-muted text-marke"><Icon className="h-4 w-4" aria-hidden="true" /></span>
                <span className="text-foreground/90">{text}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-muted-foreground">P1 Performance Center · Eutingen</p>
      </section>

      {/* Anmeldung */}
      <section className="flex items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-4 text-center lg:text-left">
            <img src="/logo-black-kompakt.png" alt="P1 Performance Center" className="mx-auto h-12 w-auto object-contain dark:hidden lg:hidden" />
            <img src="/logo-weiss-kompakt.png" alt="" className="mx-auto hidden h-12 w-auto object-contain dark:block lg:!hidden" />
            <div>
              <p className="text-sm text-muted-foreground">Trainer-Bereich</p>
              <h2 className="text-3xl font-extrabold tracking-tight">Coach-Login</h2>
            </div>
          </div>

          <Card>
            <CardContent className="space-y-4 p-6">
              <div role="tablist" aria-label="Anmeldeweg" className="flex gap-1 rounded-lg bg-muted p-1">
                {reiter('app', 'App-Zugang', 'coach-weg-app')}
                {reiter('code', 'Code per E-Mail', 'coach-weg-code')}
              </div>

              {weg === 'app' ? (
                <form onSubmit={mitApp} className="space-y-4">
                  {emailFeld}
                  <div className="space-y-1.5">
                    <Label htmlFor="coach-pin">PIN</Label>
                    <Input id="coach-pin" type="password" inputMode="numeric" autoComplete="current-password" required value={pin} maxLength={4}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="••••"
                      className="h-11 text-lg tracking-[0.5em]" data-testid="coach-pin" />
                    <p className="text-xs text-muted-foreground">Wie in der App: die letzten vier Ziffern deiner Handynummer.</p>
                  </div>
                  <Button type="submit" className="h-11 w-full" disabled={busy || !email || pin.length < 4} data-testid="coach-app-anmelden">
                    {busy ? 'Einen Moment…' : 'Anmelden'}
                  </Button>
                </form>
              ) : schritt === 1 ? (
                <form onSubmit={anfordern} className="space-y-4">
                  <p className="text-sm text-muted-foreground">Für Trainer ohne App-Konto: Wir schicken dir einen Code an deine E-Mail.</p>
                  {emailFeld}
                  <Button type="submit" className="h-11 w-full" disabled={busy || !email} data-testid="coach-code-anfordern">Code schicken</Button>
                </form>
              ) : (
                <form onSubmit={bestaetigen} className="space-y-4">
                  <p className="text-sm text-muted-foreground">Wenn es für <strong className="text-foreground">{email}</strong> einen Trainer-Zugang gibt, ist ein 6-stelliger Code unterwegs. Er gilt 15 Minuten.</p>
                  <div className="space-y-1.5">
                    <Label htmlFor="coach-code">Code aus der E-Mail</Label>
                    <Input id="coach-code" inputMode="numeric" autoComplete="one-time-code" required value={code} placeholder="000000"
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="h-11 text-center text-lg tracking-[0.4em]" data-testid="coach-code" />
                  </div>
                  <Button type="submit" className="h-11 w-full" disabled={busy || code.length < 6} data-testid="coach-anmelden">Anmelden</Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => { setSchritt(1); setCode(''); }}>Andere E-Mail</Button>
                </form>
              )}
              {meldung && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">{meldung}</p>}
            </CardContent>
          </Card>
          <p className="text-center text-xs text-muted-foreground">Kein Zugang oder PIN vergessen? Sprich das Team am Empfang an.</p>
        </div>
      </section>
    </main>
  );
}

/* ── Sätze bearbeiten (Korrigieren und Nachtragen) ── */

function SatzEditor({ start, plan, labels, onAbbrechen, onSpeichern, busy }) {
  // start: [{slot, uebung, saetze}|{slot, ergebnis}] ; plan: Plan-Blöcke der Einheit (für Übungsauswahl)
  const [bloecke, setBloecke] = useState(() => start.map((b) => (b.ergebnis != null
    ? { ...b }
    : { ...b, saetze: b.saetze.map((s) => ({ kg: String(s.kg).replace('.', ','), wdh: String(s.wdh), rpe: s.rpe == null ? '' : String(s.rpe).replace('.', ',') })) })));
  const optionen = useMemo(() => {
    const o = [];
    KRAFT.forEach((slot) => {
      const p = plan?.[slot] || {};
      (slot === 'zubringer' ? (p.uebungen || []) : (p.uebung ? [p.uebung] : [])).forEach((u) => o.push({ slot, uebung: u }));
    });
    return o;
  }, [plan]);
  const fehlend = optionen.filter((o) => !bloecke.some((b) => b.slot === o.slot && b.uebung === o.uebung));
  const condDa = bloecke.some((b) => b.slot === 'conditioning');
  const setze = (i, fn) => setBloecke((bs) => bs.map((b, j) => (j === i ? fn(b) : b)));

  const absenden = () => {
    const raus = [];
    for (const b of bloecke) {
      if (b.slot === 'conditioning') { if (b.ergebnis.trim()) raus.push({ slot: 'conditioning', ergebnis: b.ergebnis.trim() }); continue; }
      const saetze = b.saetze.filter((s) => s.wdh !== '').map((s) => ({ kg: zahl(s.kg || '0'), wdh: zahl(s.wdh), rpe: s.rpe === '' ? null : zahl(s.rpe) }));
      if (saetze.some((s) => Number.isNaN(s.kg) || Number.isNaN(s.wdh) || Number.isNaN(s.rpe ?? 0))) { toast.error(`${b.uebung}: bitte nur Zahlen eintragen`); return; }
      if (saetze.length) raus.push({ slot: b.slot, uebung: b.uebung, saetze });
    }
    if (!raus.length) { toast.error('Mindestens eine Übung mit einem Satz eintragen'); return; }
    onSpeichern(raus);
  };

  return (
    <div className="space-y-3 rounded-lg border border-border p-3" data-testid="coach-editor">
      {bloecke.map((b, i) => (
        <fieldset key={`${b.slot}-${b.uebung || ''}`} className="space-y-1.5">
          <legend className="text-xs font-medium">{labels?.[b.slot] || b.slot}{b.uebung ? ` · ${b.uebung}` : ''}</legend>
          {b.slot === 'conditioning' ? (
            <Input aria-label="Ergebnis" value={b.ergebnis} maxLength={200} onChange={(e) => setze(i, (x) => ({ ...x, ergebnis: e.target.value }))} placeholder="z. B. 6 Runden + 4" />
          ) : (
            <>
              {b.saetze.map((s, k) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="w-6 text-xs text-muted-foreground">{k + 1}.</span>
                  <Input aria-label={`Satz ${k + 1}, Kilogramm`} inputMode="decimal" className="h-8 w-20" value={s.kg} placeholder="kg"
                    onChange={(e) => setze(i, (x) => ({ ...x, saetze: x.saetze.map((y, m) => (m === k ? { ...y, kg: e.target.value } : y)) }))} />
                  <span className="text-xs text-muted-foreground">×</span>
                  <Input aria-label={`Satz ${k + 1}, Wiederholungen`} inputMode="numeric" className="h-8 w-16" value={s.wdh} placeholder="Wdh"
                    onChange={(e) => setze(i, (x) => ({ ...x, saetze: x.saetze.map((y, m) => (m === k ? { ...y, wdh: e.target.value.replace(/\D/g, '') } : y)) }))} />
                  <Input aria-label={`Satz ${k + 1}, RPE`} inputMode="decimal" className="h-8 w-16" value={s.rpe} placeholder="RPE"
                    onChange={(e) => setze(i, (x) => ({ ...x, saetze: x.saetze.map((y, m) => (m === k ? { ...y, rpe: e.target.value } : y)) }))} />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label={`Satz ${k + 1} entfernen`}
                    onClick={() => setze(i, (x) => ({ ...x, saetze: x.saetze.filter((_, m) => m !== k) }))}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="gap-1" disabled={b.saetze.length >= 15}
                onClick={() => setze(i, (x) => ({ ...x, saetze: [...x.saetze, x.saetze.length ? { ...x.saetze[x.saetze.length - 1] } : { kg: '', wdh: '', rpe: '' }] }))}>
                <Plus className="w-3.5 h-3.5" /> Satz
              </Button>
            </>
          )}
        </fieldset>
      ))}
      {(fehlend.length > 0 || (!condDa && plan?.conditioning)) && (
        <div className="flex flex-wrap gap-1.5">
          {fehlend.map((o) => (
            <Button key={`${o.slot}-${o.uebung}`} type="button" size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => setBloecke((bs) => [...bs, { slot: o.slot, uebung: o.uebung, saetze: [{ kg: '', wdh: '', rpe: '' }] }])}>+ {o.uebung}</Button>
          ))}
          {!condDa && plan?.conditioning && (
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBloecke((bs) => [...bs, { slot: 'conditioning', ergebnis: '' }])}>
              + {labels?.conditioning || 'Conditioning'}
            </Button>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={absenden} disabled={busy} data-testid="coach-editor-speichern">Speichern</Button>
        <Button size="sm" variant="ghost" onClick={onAbbrechen}>Abbrechen</Button>
      </div>
    </div>
  );
}

/* ── Athleten ── */

function AthletenListe({ onWahl, q }) {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { coach.get('/athleten').then((r) => setListe(r.data.athleten)).catch((e) => setErr(fehler(e, 'Liste nicht geladen'))); }, []);
  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!liste) return <div className="space-y-3" aria-busy="true"><div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-[14px]" />)}</div><Skeleton className="h-64 rounded-[14px]" /></div>;
  if (!liste.length) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Noch keine Athleten: Hier erscheinen die Teilnehmer der laufenden Kurs-Zyklen, Personen mit eigenem Plan und dir zugeordnete Kunden.</CardContent></Card>;
  const f = liste.filter((a) => a.name.toLowerCase().includes((q || '').trim().toLowerCase()));
  const vor7 = plusTage(heuteIso(), -7);
  // aktiv = Kraftlog-Eintrag ODER Studio-Besuch (Check-in) in den letzten sieben Tagen
  const zuletzt = (a) => [a.letzte, a.letzter_besuch].filter(Boolean).map((x) => String(x).slice(0, 10)).sort().pop();
  const aktiv = liste.filter((a) => (zuletzt(a) || '') >= vor7).length;
  const einheiten = liste.reduce((n, a) => n + (a.einheiten || 0), 0);
  const offen = liste.reduce((n, a) => n + (a.ohne_kommentar || 0), 0);
  const antworten = liste.reduce((n, a) => n + (a.antworten_offen || 0), 0);
  const max = Math.max(...liste.map((a) => a.einheiten || 0), 1);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="coach-kacheln">
        <Kachel label="Athleten" wert={liste.length} sub="in deinem Bereich" icon={Users} />
        <Kachel label="Aktiv" wert={aktiv} sub="in den letzten 7 Tagen eingetragen oder im Studio" icon={CalendarDays} verzug={60} />
        <Kachel label="Einheiten" wert={einheiten} sub="insgesamt eingetragen" icon={Layers} verzug={120} />
        {antworten > 0
          ? <Kachel label="Antworten offen" wert={antworten} sub="Mitglieder haben dir geantwortet" icon={MessageSquare} verzug={180} />
          : <Kachel label="Ohne Kommentar" wert={offen} sub={offen ? 'warten auf deine Rückmeldung' : 'alles kommentiert'} icon={MessageSquare} verzug={180} />}
      </div>
      <Abschnitt titel="Athleten" sub={q ? `${f.length} von ${liste.length} · Suche „${q}“` : 'Offene Antworten zuerst, dann zuletzt aktive'}>
        {!f.length && <p className="py-6 text-center text-sm text-muted-foreground">Niemand gefunden.</p>}
        <ul className="-mx-2 divide-y divide-border/60">
          {f.map((a) => (
            <li key={a.member_id}>
              <button type="button" onClick={() => onWahl(a.member_id)} data-testid={`coach-athlet-${a.member_id}`}
                className="group flex w-full items-center gap-3 rounded-[10px] px-2 py-3 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-extrabold text-muted-foreground" aria-hidden="true">{kuerzel(a.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-bold break-words">{a.name || 'Ohne Namen'}</span>
                    {(a.kurse || []).map((k) => <Badge key={k.id} variant="secondary" className="text-[10px] font-semibold">{k.name}</Badge>)}
                    {a.videos_neu > 0 && <Badge variant="outline" className="text-[10px] font-bold">{a.videos_neu === 1 ? 'neues Video' : `${a.videos_neu} neue Videos`}</Badge>}
                    {a.chat_ungelesen > 0 && <Badge className="border-transparent bg-primary text-[10px] font-bold text-primary-foreground hover:bg-primary">{a.chat_ungelesen === 1 ? 'neue Nachricht' : `${a.chat_ungelesen} neue Nachrichten`}</Badge>}
                    {a.bestwerte_neu > 0 && <Badge variant="outline" className="text-[10px] font-bold">{a.bestwerte_neu === 1 ? 'neuer Bestwert' : `${a.bestwerte_neu} Bestwerte`}</Badge>}
                    {a.antworten_offen > 0 && <Badge className="border-transparent bg-primary text-[10px] font-bold text-primary-foreground hover:bg-primary">{a.antworten_offen === 1 ? 'Antwort offen' : `${a.antworten_offen} Antworten offen`}</Badge>}
                    {a.ohne_kommentar > 0 && <Badge className="border-transparent bg-marke/15 text-[10px] font-bold text-marke hover:bg-marke/15">{a.ohne_kommentar} ohne Kommentar</Badge>}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {a.einheiten ? `zuletzt ${datumDe(a.letzte)}` : 'noch nichts eingetragen'}
                    {a.letzter_besuch && ` · im Studio ${datumDe(a.letzter_besuch)}`}
                    {a.plan && ` · Plan ${a.plan.quote} %`}
                    {a.eigene_plaene.length > 0 && ` · eigener Plan: ${a.eigene_plaene.map((p) => p.name).join(', ')}`}
                  </span>
                  <span className="mt-2 block h-1 overflow-hidden rounded-full bg-muted sm:hidden"><span className="block h-full rounded-full bg-marke" style={{ width: `${((a.einheiten || 0) / max) * 100}%` }} /></span>
                </span>
                <span className="hidden w-28 shrink-0 sm:block" aria-hidden="true">
                  <span className="block h-1.5 overflow-hidden rounded-full bg-muted"><span className="coach-balken block h-full rounded-full bg-marke" style={{ width: `${((a.einheiten || 0) / max) * 100}%` }} /></span>
                </span>
                <span className="w-14 shrink-0 text-right"><span className="block text-lg font-extrabold leading-none tabular-nums">{a.einheiten || 0}</span><span className="text-[10px] font-semibold text-muted-foreground">Einheiten</span></span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </Abschnitt>
    </div>
  );
}

/* Woche und Tag per Auswahl (Betreiber 10.10.2026: „falsches Datum und falsche Trainingswoche“): Woche 1–n mit
   Datumsbereich, Tag Mo–So als Knöpfe — das Datum ergibt sich daraus und liegt immer in der gewählten Woche. */
const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const TAG_LANG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const plusTage = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const kurz = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const heuteIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const tagAusDatum = (montag, datum) => (montag && datum ? Math.round((new Date(`${datum}T12:00:00Z`) - new Date(`${montag}T12:00:00Z`)) / 86400000) : -1);

function WocheTagWahl({ z, woche, datum, onWoche, onDatum, idPrefix }) {
  const montag = z?.wochen?.[woche - 1]?.montag;
  const tag = tagAusDatum(montag, datum);
  const heute = heuteIso();
  const sel = 'flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm';
  return (
    <div className="space-y-2">
      <div className="space-y-1"><Label htmlFor={`${idPrefix}-woche`}>Trainingswoche</Label>
        <select id={`${idPrefix}-woche`} className={sel} value={woche} onChange={(e) => onWoche(Number(e.target.value))}>
          {(z?.wochen || []).map((w) => (
            <option key={w.woche} value={w.woche} disabled={w.montag > heute}>
              Woche {w.woche} · {kurz(w.montag)}–{kurz(plusTage(w.montag, 6))}{w.montag > heute ? ' (noch nicht)' : ''}{w.woche === z.aktuelle_woche ? ' · aktuell' : ''}
            </option>
          ))}
        </select></div>
      <fieldset>
        <legend className="text-sm font-medium mb-1">Trainingstag</legend>
        <div className="grid grid-cols-7 gap-1" role="radiogroup" aria-label="Trainingstag">
          {TAGE.map((t, i) => {
            const iso = montag ? plusTage(montag, i) : '';
            const zukunft = iso > heute;
            const an = i === tag;
            return (
              <button key={t} type="button" role="radio" aria-checked={an} aria-label={`${TAG_LANG[i]}, ${iso ? kurz(iso) : ''}`} disabled={!montag || zukunft}
                onClick={() => onDatum(iso)} data-testid={`${idPrefix}-tag-${i}`}
                className={`rounded-md border py-1.5 text-center text-sm leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 ${an ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
                <span className="block font-medium">{t}</span><span className="block text-[11px] opacity-80">{iso ? kurz(iso) : ''}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      {tag >= 0 && tag <= 6 && <p className="text-sm" data-testid={`${idPrefix}-zusammenfassung`}>Wird eingetragen: <strong>Woche {woche}, {TAG_LANG[tag]} {datumDe(datum).split(', ').pop()}{datum.slice(0, 4)}</strong></p>}
    </div>
  );
}

function Nachtragen({ athlet, onFertig }) {
  // Auswahl Plan → Woche → Einheit → Datum, dann der Editor mit den geplanten Übungen
  const plaene = useMemo(() => {
    const m = new Map();
    // Kurs-Zyklen zuerst: dort trainieren die meisten, auch ohne bisherigen Eintrag
    (athlet.kurse || []).forEach((k) => m.set(k.id, k.name));
    athlet.eigene_plaene.forEach((p) => m.set(p.id, p.name));
    athlet.eintraege.forEach((e) => m.set(e.zyklus_id, e.zyklus_name));
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [athlet]);
  const [zid, setZid] = useState(plaene[0]?.id || '');
  const [z, setZ] = useState(null);
  const [woche, setWoche] = useState(1);
  const [einheit, setEinheit] = useState('');
  const [datum, setDatum] = useState('');
  const [busy, setBusy] = useState(false);
  // Vorgewählt: der geplante Trainingstag der Einheit (sonst Montag), nie in der Zukunft
  const standardDatum = (doc, w, key) => {
    const montag = doc?.wochen?.[w - 1]?.montag;
    if (!montag) return '';
    const plan = (doc.einheiten.find((e) => e.key === key)?.wochentage || [])[0];
    const iso = plusTage(montag, Number.isInteger(plan) ? plan : 0);
    return iso > heuteIso() ? '' : iso;
  };
  useEffect(() => {
    if (!zid) return;
    coach.get(`/zyklen/${zid}`).then((r) => {
      const w = r.data.aktuelle_woche || 1; const key = r.data.einheiten[0]?.key || '';
      setZ(r.data); setWoche(w); setEinheit(key); setDatum(standardDatum(r.data, w, key));
    }).catch((e) => toast.error(fehler(e, 'Plan nicht geladen')));
  }, [zid]);
  if (!plaene.length) return <p className="text-sm text-muted-foreground">Für diese Person gibt es keinen Plan zum Nachtragen.</p>;
  const plan = z?.wochen?.[woche - 1]?.einheiten?.[einheit];
  const speichern = async (bloecke) => {
    setBusy(true);
    try { await coach.put(`/athleten/${athlet.member_id}/eintraege`, { zyklus_id: zid, woche, einheit, datum, bloecke }); toast.success('Eingetragen'); onFertig(); }
    catch (e) { toast.error(fehler(e, 'Nicht gespeichert')); }
    setBusy(false);
  };
  const sel = 'flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm';
  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="coach-nachtragen">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1 col-span-2 sm:col-span-1"><Label htmlFor="n-plan">Plan</Label>
          <select id="n-plan" className={sel} value={zid} onChange={(e) => setZid(e.target.value)}>{plaene.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div className="space-y-1 col-span-2 sm:col-span-1"><Label htmlFor="n-einheit">Einheit</Label>
          <select id="n-einheit" className={sel} value={einheit} onChange={(e) => { setEinheit(e.target.value); setDatum(standardDatum(z, woche, e.target.value)); }}>
            {(z?.einheiten || []).map((e) => <option key={e.key} value={e.key}>{e.key} · {e.titel}{(e.wochentage || []).length ? ` (${e.wochentage.map((t) => TAGE[t]).join('/')})` : ''}</option>)}</select></div>
      </div>
      {z && <WocheTagWahl z={z} woche={woche} datum={datum} idPrefix="n" onWoche={(w) => { setWoche(w); setDatum(standardDatum(z, w, einheit)); }} onDatum={setDatum} />}
      {!datum && z && <p className="text-sm text-amber-700">Bitte den Trainingstag wählen.</p>}
      {plan && datum && <SatzEditor key={`${zid}-${woche}-${einheit}`} start={[]} plan={plan} labels={z.labels} busy={busy} onAbbrechen={onFertig} onSpeichern={speichern} />}
    </CardContent></Card>
  );
}

/* W3.1 Plan-Editor auf /coach: Endpunkte unter /api/coach/planer (Rechte wie „Woche anpassen“). */
const PLANER = '/planer';

function CoachPersonFeld({ wert, onWahl }) {
  const [liste, setListe] = useState([]);
  useEffect(() => { coach.get('/athleten').then((r) => setListe(r.data.athleten || [])).catch(() => setListe([])); }, []);
  return (
    <select aria-label="Person" value={wert?.id || ''} onChange={(e) => { const a = liste.find((x) => x.member_id === e.target.value); onWahl(a ? { id: a.member_id, name: a.name } : null); }}
      className="min-h-[44px] rounded-md border border-input bg-background px-2 text-sm">
      <option value="">ohne Person</option>
      {liste.map((a) => <option key={a.member_id} value={a.member_id}>{a.name || a.member_id}</option>)}
    </select>
  );
}

/* `eingebettet` (Kundenakte /coach/kunden/:id, Reiter Training): ohne „Alle Athleten“ und ohne Namenszeile — den Kopf
   zeigt die Akte. Alles andere (Nachtragen, Korrigieren, Woche/Tag, Kommentar, Rückmeldung, Gewohnheiten, Videos, Tests,
   Fotos, Chat) bleibt wie in der Athlet-Ansicht. Gehört die Person nicht zu den Athleten (403), steht ein Satz da. */
function AthletAnsicht({ mid, onZurueck, eingebettet = false, gehe, onPlan, ohnePlaene = false }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [bearbeite, setBearbeite] = useState(null);
  const [plaene, setPlaene] = useState({});
  const [nachtragen, setNachtragen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [umzug, setUmzug] = useState(null);         // { id, zyklus_id, woche, datum }
  const lade = useCallback(() => {
    coach.get(`/athleten/${mid}`).then((r) => setD(r.data)).catch((e) => setErr(
      eingebettet && e?.response?.status === 403 ? 'Kein Kraftlog und kein Trainingsplan in deinem Bereich – die Person trainiert (noch) nicht in deinen Zyklen oder Kursen.' : fehler(e, 'Nicht geladen')));
  }, [mid, eingebettet]);
  useEffect(() => { lade(); }, [lade]);
  const gw = useMemo(() => ({
    laden: () => coach.get(`/athleten/${mid}/gewohnheiten`),
    anlegen: (body) => coach.post(`/athleten/${mid}/gewohnheiten`, body),
    beenden: (gid) => coach.delete(`/gewohnheiten/${gid}`),
    aktivSetzen: (gid, aktiv) => coach.patch(`/gewohnheiten/${gid}/aktiv`, { aktiv }),
  }), [mid]);
  const chat = useMemo(() => ({
    laden: () => coach.get(`/athleten/${mid}/chat`),
    senden: (text) => coach.post(`/athleten/${mid}/chat`, { text }),
    gelesen: () => coach.post(`/athleten/${mid}/chat/gelesen`),
    // W2.6 (29.10.2026): Video-/Audio-Feedback und Sprachnachrichten im Athlet-Chat
    leadId: mid,
    audio: (nid) => coach.get(`/athleten/${mid}/chat/${nid}/audio`, { responseType: 'blob', timeout: 60000 }),
    medien: {
      hochladen: (fd, cfg = {}) => coach.post(`/athleten/${mid}/medien-feedback`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000, ...cfg }),
      datei: (fid) => coach.get(`/athleten/${mid}/medien-feedback/${fid}/datei`, { responseType: 'blob', timeout: 180000 }),
    },
  }), [mid]);
  const tests = useMemo(() => ({
    laden: () => coach.get(`/athleten/${mid}/tests`),
    eintragen: (b) => coach.post(`/athleten/${mid}/tests`, b),
    loeschen: (m) => coach.delete(`/athleten/${mid}/tests/${m}`),
  }), [mid]);
  const videos = useMemo(() => ({
    laden: () => coach.get(`/athleten/${mid}/videos`),
    videoLaden: (vid) => coach.get(`/athleten/${mid}/videos/${vid}`, { responseType: 'blob' }),
    kommentieren: (vid, text) => coach.post(`/athleten/${mid}/videos/${vid}/kommentar`, { text }),
  }), [mid]);
  const testimonial = useMemo(() => testimonialCoach(coach, mid), [mid]);   // Coach-Cockpit W1.7
  const fotos = useMemo(() => ({
    laden: () => coach.get(`/athleten/${mid}/fotos`),
    bildLaden: (fid) => coach.get(`/athleten/${mid}/fotos/${fid}`, { responseType: 'blob' }),
  }), [mid]);

  const korrigieren = async (e) => {
    if (!plaene[e.zyklus_id]) {
      try { const r = await coach.get(`/zyklen/${e.zyklus_id}`); setPlaene((p) => ({ ...p, [e.zyklus_id]: r.data })); }
      catch { /* ohne Plan: nur vorhandene Blöcke bearbeitbar */ }
    }
    setBearbeite(e.id);
  };
  const umziehenStart = async (e) => {
    if (!plaene[e.zyklus_id]) {
      try { const r = await coach.get(`/zyklen/${e.zyklus_id}`); setPlaene((p) => ({ ...p, [e.zyklus_id]: r.data })); }
      catch (x) { toast.error(fehler(x, 'Plan nicht geladen')); return; }
    }
    setBearbeite(null); setUmzug({ id: e.id, zyklus_id: e.zyklus_id, woche: e.woche, datum: e.datum });
  };
  const umziehen = async () => {
    setBusy(true);
    try {
      await coach.patch(`/eintraege/${umzug.id}/verschieben`, { woche: umzug.woche, datum: umzug.datum });
      toast.success(`Verschoben auf Woche ${umzug.woche}, ${datumDe(umzug.datum)}`); setUmzug(null); lade();
    } catch (x) { toast.error(fehler(x, 'Nicht verschoben')); }
    setBusy(false);
  };
  const speichern = async (e, bloecke) => {
    setBusy(true);
    try {
      await coach.put(`/athleten/${mid}/eintraege`, { zyklus_id: e.zyklus_id, woche: e.woche, einheit: e.einheit, datum: e.datum, bloecke });
      toast.success('Korrektur gespeichert'); setBearbeite(null); lade();
    } catch (x) { toast.error(fehler(x, 'Nicht gespeichert')); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      {!eingebettet && <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground" onClick={onZurueck}><ArrowLeft className="w-4 h-4" /> Alle Athleten</Button>}
      {err && (eingebettet
        ? <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" role="status">{err}</CardContent></Card>
        : <p className="text-sm text-destructive" role="alert">{err}</p>)}
      {!d && !err && <div className="space-y-2" aria-busy="true">{[0, 1].map((i) => <Skeleton key={i} className="h-24" />)}</div>}
      {d && (
        <>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex min-w-0 items-center gap-3">
              {!eingebettet && <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-extrabold text-muted-foreground" aria-hidden="true">{kuerzel(d.name)}</span>}
              <div className="min-w-0">
                {!eingebettet && <h2 className="text-2xl font-extrabold tracking-tight break-words">{d.name}</h2>}
                <p className="text-sm text-muted-foreground">{[...(d.kurse || []).map((k) => k.name), ...d.eigene_plaene.map((p) => p.name)].join(' · ') || 'ohne laufenden Plan'}</p>
              </div>
            </div>
            <Button className="gap-1.5 font-bold" onClick={() => setNachtragen((v) => !v)} data-testid="coach-nachtragen-knopf"><Plus className="w-4 h-4" /> Einheit nachtragen</Button>
          </div>
          <AthletZahlen d={d} />
          <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
            <CoachChat key={`c-${mid}`} {...chat} testId="coach-chat" />
            <CoachKundeninfo coach={coach} leadId={mid} gehe={gehe} />
          </div>
          <Besuche besuche={d.besuche || []} eigene={d.aktivitaeten || []} />
          <Gewohnheiten key={mid} {...gw} testId="coach-gewohnheiten" />
          {!ohnePlaene && <PersonenPlaene key={`p-${mid}`} client={coach} basis={PLANER} leadId={mid} onOeffnen={onPlan} />}
          <TechnikVideos key={`v-${mid}`} {...videos} testId="coach-videos" />
          <Leistungstests key={`t-${mid}`} {...tests} testId="coach-tests" />
          <Fortschrittsfotos key={`f-${mid}`} {...fotos} testId="coach-fotos" />
          <TestimonialKarte key={`tm-${mid}`} client={testimonial} testId="coach-testimonial" />
          {nachtragen && <Nachtragen athlet={d} onFertig={() => { setNachtragen(false); lade(); }} />}
          {!d.eintraege.length && !nachtragen && <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Noch nichts eingetragen.</CardContent></Card>}
          {d.eintraege.map((e) => (
            <Card key={e.id} data-testid={`coach-eintrag-${e.id}`}>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-base font-extrabold">{datumDe(e.datum)}</span>
                  <span className="text-muted-foreground font-normal break-words">{e.zyklus_name} · Woche {e.woche} · Einheit {e.einheit}</span>
                  {e.laufend && <Badge variant="outline" className="text-[10px]">läuft noch</Badge>}
                  {(e.neue_bestwerte || []).map((b) => (
                    <Badge key={b.uebung} className="border-transparent bg-primary text-[10px] font-bold text-primary-foreground hover:bg-primary">
                      Bestwert {b.uebung} +{String(b.plus).replace('.', ',')} kg
                    </Badge>
                  ))}
                  {e.geaendert_von?.art === 'coach' && <Badge variant="outline" className="text-[10px]">geändert von {e.geaendert_von.name}, {zeitDe(e.geaendert_von.am)}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {bearbeite === e.id ? (
                  <SatzEditor start={e.bloecke} plan={plaene[e.zyklus_id]?.wochen?.[e.woche - 1]?.einheiten?.[e.einheit]}
                    labels={plaene[e.zyklus_id]?.labels} busy={busy} onAbbrechen={() => setBearbeite(null)} onSpeichern={(b) => speichern(e, b)} />
                ) : (
                  <>
                    {e.plan_gegen_ist ? <div>{e.plan_gegen_ist.map((z) => <Block key={z.slot} z={z} />)}</div> : null}
                    {umzug?.id === e.id ? (
                      <div className="rounded-md border border-border p-3 space-y-3" data-testid={`coach-umzug-${e.id}`}>
                        <WocheTagWahl z={plaene[e.zyklus_id]} woche={umzug.woche} datum={umzug.datum} idPrefix={`u-${e.id}`}
                          onWoche={(w) => { const m = plaene[e.zyklus_id]?.wochen?.[w - 1]?.montag; const t = Math.max(0, tagAusDatum(plaene[e.zyklus_id]?.wochen?.[umzug.woche - 1]?.montag, umzug.datum)); const iso = m ? plusTage(m, t) : ''; setUmzug({ ...umzug, woche: w, datum: iso > heuteIso() ? '' : iso }); }}
                          onDatum={(d) => setUmzug({ ...umzug, datum: d })} />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setUmzug(null)}>Abbrechen</Button>
                          <Button size="sm" disabled={busy || !umzug.datum || (umzug.woche === e.woche && umzug.datum === e.datum)} onClick={umziehen}>Verschieben</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => umziehenStart(e)} data-testid={`coach-umziehen-${e.id}`}><CalendarDays className="w-3.5 h-3.5" /> Woche/Tag ändern</Button>
                        <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => korrigieren(e)} data-testid={`coach-korrigieren-${e.id}`}><Pencil className="w-3.5 h-3.5" /> Korrigieren</Button>
                      </div>
                    )}
                  </>
                )}
                {e.notiz && <p className="text-sm break-words"><span className="text-xs text-muted-foreground">Notiz des Mitglieds: </span>{e.notiz}</p>}
                <Kommentar eintrag={e} hinweis="Kommentar gespeichert – das Mitglied sieht ihn in der App"
                  speichern={(eid, text) => coach.put(`/eintraege/${eid}/kommentar`, { text })}
                  onGespeichert={(neu) => setD((x) => ({ ...x, eintraege: x.eintraege.map((y) => (y.id === neu.id ? { ...y, ...neu } : y)) }))} />
                <Faden eintrag={e}
                  antworten={(eid, text) => coach.post(`/eintraege/${eid}/antwort`, { text })}
                  gelesen={(eid) => coach.post(`/eintraege/${eid}/gelesen`)}
                  onGespeichert={(neu) => setD((x) => ({ ...x, eintraege: x.eintraege.map((y) => (y.id === neu.id ? { ...y, ...neu } : y)) }))} />
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

/* Studio-Besuche (13.10.2026, replica/coaching-recherche/fixes.md #2): Check-ins der letzten acht Wochen, mit Kurs, wenn er sich
   zuordnen lässt — wer zum Kurs kommt und nichts einträgt, ist trotzdem da. Kein Kraftlog-Eintrag, zählt nicht als Einheit. */
function Besuche({ besuche, eigene = [] }) {
  if (!besuche.length && !eigene.length) return null;
  const kurse = besuche.filter((b) => b.kurs).length;
  return (
    <Abschnitt titel="Im Studio und unterwegs" sub={`${besuche.length} Check-ins in acht Wochen · ${kurse} davon im Kurs · zählen nicht als Kraftlog-Einheit`}>
      <ul className="flex flex-wrap gap-2" data-testid="coach-besuche">
        {besuche.slice(0, 24).map((b, i) => (
          <li key={`${b.datum}-${b.zeit}-${i}`} className="rounded-[10px] bg-muted/70 px-3 py-2 text-sm">
            <span className="font-bold tabular-nums">{datumDe(b.datum)}</span>
            <span className="text-muted-foreground"> · {b.zeit} · {b.kurs || 'freies Training'}</span>
          </li>
        ))}
      </ul>
      {eigene.length > 0 && (
        <>
          <p className="mt-4 mb-2 text-sm font-bold">Eigene Einheiten <span className="font-normal text-muted-foreground">· außerhalb des Plans, in der App eingetragen</span></p>
          <ul className="flex flex-wrap gap-2" data-testid="coach-eigene">
            {eigene.slice(0, 24).map((a) => (
              <li key={a.id} className="rounded-[10px] bg-muted/70 px-3 py-2 text-sm">
                <span className="font-bold tabular-nums">{datumDe(a.datum)}</span>
                <span className="text-muted-foreground"> · {a.titel} · {a.dauer_min} Min{a.distanz_km ? ` · ${String(a.distanz_km).replace('.', ',')} km` : ''}{a.anstrengung ? ` · ${a.anstrengung}` : ''}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Abschnitt>
  );
}

/* Kennzahlen, Verlauf und Bestwerte eines Athleten — alles aus den Einträgen, die die Seite ohnehin lädt. */
function AthletZahlen({ d }) {
  const { wochen, letzte, offen } = useMemo(() => {
    const heute = heuteIso();
    const wt = (new Date(`${heute}T12:00:00Z`).getUTCDay() + 6) % 7;
    const montag = plusTage(heute, -wt);
    const w = Array.from({ length: 12 }, (_, i) => { const m = plusTage(montag, -7 * (11 - i)); return { montag: m, label: kurz(m), einheiten: 0 }; });
    d.eintraege.forEach((e) => { const tag = String(e.datum).slice(0, 10); const z = w.find((x) => tag >= x.montag && tag <= plusTage(x.montag, 6)); if (z) z.einheiten += 1; });
    return { wochen: w, letzte: d.eintraege.map((e) => String(e.datum).slice(0, 10)).sort().pop(), offen: d.eintraege.filter((e) => !e.coach_kommentar && !e.laufend).length };
  }, [d]);
  const in4 = wochen.slice(-4).reduce((n, w) => n + w.einheiten, 0);
  const best = d.bestwerte.slice(0, 6);
  const maxBest = Math.max(...best.map((b) => b.e1rm || 0), 1);
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {d.plan
          ? <Kachel label="Plan eingetragen" wert={`${d.plan.quote} %`} sub={`${d.plan.ist} von ${d.plan.soll} fälligen Einheiten, ${d.plan.wochen} Wochen`} icon={Layers} />
          : <Kachel label="Einheiten" wert={d.eintraege.length} sub="insgesamt eingetragen" icon={Layers} />}
        <Kachel label="Letzte 4 Wochen" wert={in4} sub={`${String((in4 / 4).toFixed(1)).replace('.', ',')} je Woche`} icon={CalendarDays} verzug={60} />
        <Kachel label="Zuletzt" wert={letzte ? kurz(letzte) : '–'} sub={letzte ? datumDe(letzte).split(',')[0] : 'noch nichts eingetragen'} icon={Users} verzug={120} />
        <Kachel label="Ohne Kommentar" wert={offen} sub={offen ? 'warten auf deine Rückmeldung' : 'alles kommentiert'} icon={MessageSquare} verzug={180} />
      </div>
      {d.eintraege.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Abschnitt titel="Einheiten je Woche" sub="Die letzten zwölf Wochen" className="lg:col-span-2"
            rechts={<p className="text-right"><span className="block text-3xl font-extrabold leading-none tabular-nums">{wochen[11].einheiten}</span><span className="text-xs font-semibold text-muted-foreground">diese Woche</span></p>}>
            <div className="h-48" role="img" aria-label={`Einheiten je Woche: ${wochen.map((w) => `${w.label} ${w.einheiten}`).join(', ')}`}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={wochen} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                  <defs><linearGradient id="coach-flaeche" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.28} /><stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} /></linearGradient></defs>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} interval={2} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }} formatter={(v) => [v, 'Einheiten']} labelFormatter={(l) => `Woche ab ${l}`}
                    contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 10, fontSize: 12, color: 'hsl(var(--popover-foreground))' }} />
                  <Area type="monotone" dataKey="einheiten" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#coach-flaeche)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Abschnitt>
          <Abschnitt titel="Bestwerte" sub="Geschätztes Maximum (e1RM)">
            {best.length ? <div className="space-y-3.5">{best.map((b) => <Balken key={b.uebung} label={b.uebung} wert={`${String(b.e1rm).replace('.', ',')} kg`} anteil={(b.e1rm / maxBest) * 100} />)}</div>
              : <p className="text-sm text-muted-foreground">Noch keine Kraftwerte.</p>}
          </Abschnitt>
        </div>
      )}
    </>
  );
}

/* ── Bestenliste (Betreiber 12.10.2026: „eine Performance von allen") ── */

const kg = (v) => `${Number(v).toLocaleString('de-DE', { maximumFractionDigits: 1 })} kg`;
const tonnen = (v) => (v >= 1000 ? `${(v / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} t` : `${Math.round(v).toLocaleString('de-DE')} kg`);

function Rangliste({ zeilen, wert, einheit, zusatz, anteil, onWahl, leer }) {
  if (!zeilen.length) return <p className="py-8 text-center text-sm text-muted-foreground">{leer}</p>;
  const podest = zeilen.slice(0, 3);
  return (
    <div className="space-y-5">
      {/* Podest: die ersten drei groß, Platz 1 hervorgehoben */}
      <div className="grid gap-3 sm:grid-cols-3">
        {podest.map((z, i) => (
          <button key={z.member_id} type="button" onClick={() => onWahl(z.member_id)} style={{ animationDelay: `${i * 70}ms` }}
            className={`coach-rein rounded-[14px] border p-5 text-left transition-colors hover:border-marke/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${i === 0 ? 'border-marke/50 bg-marke/10' : 'border-border/70 bg-muted/40'}`}>
            <span className="flex items-center justify-between">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-extrabold ${i === 0 ? 'bg-marke text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
              {i === 0 && <Trophy className="h-5 w-5 text-marke" aria-hidden="true" />}
            </span>
            <span className="mt-3 block font-extrabold break-words">{z.name || 'Ohne Namen'}</span>
            <span className="mt-1 block text-3xl font-extrabold leading-none tabular-nums">{wert(z)}<span className="ml-1.5 text-sm font-semibold text-muted-foreground">{einheit}</span></span>
            <span className="mt-2 block text-xs text-muted-foreground">{zusatz(z)}</span>
          </button>
        ))}
      </div>
      {zeilen.length > 3 && (
        <ol className="-mx-2 divide-y divide-border/60" start={4}>
          {zeilen.slice(3).map((z, i) => (
            <li key={z.member_id}>
              <button type="button" onClick={() => onWahl(z.member_id)}
                className="flex w-full items-center gap-3 rounded-[10px] px-2 py-2.5 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="w-7 shrink-0 text-center text-sm font-extrabold text-muted-foreground tabular-nums">{i + 4}</span>
                <span className="min-w-0 flex-1"><span className="block font-bold break-words">{z.name || 'Ohne Namen'}</span><span className="block text-xs text-muted-foreground">{zusatz(z)}</span></span>
                <span className="hidden w-32 shrink-0 sm:block" aria-hidden="true"><span className="block h-1.5 overflow-hidden rounded-full bg-muted"><span className="coach-balken block h-full rounded-full bg-marke" style={{ width: `${anteil(z)}%` }} /></span></span>
                <span className="w-24 shrink-0 text-right text-base font-extrabold tabular-nums">{wert(z)} <span className="text-[11px] font-semibold text-muted-foreground">{einheit}</span></span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Bestenliste({ onWahl }) {
  const [wochen, setWochen] = useState(4);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [wertung, setWertung] = useState('fleiss');
  const [uebung, setUebung] = useState('');
  useEffect(() => {
    setD(null);
    coach.get('/bestenliste', { params: { wochen } }).then((r) => { setD(r.data); setUebung((u) => (r.data.kraft[u] ? u : r.data.uebungen[0]?.uebung || '')); })
      .catch((e) => setErr(fehler(e, 'Bestenliste nicht geladen')));
  }, [wochen]);
  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  const knopf = (an, text, klick, tid) => (
    <button key={text} type="button" aria-pressed={an} onClick={klick} data-testid={tid}
      className={`rounded-[10px] px-3.5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{text}</button>
  );
  const WERTUNGEN = [['fleiss', 'Fleiß', Flame], ['kraft', 'Kraft', Dumbbell], ['fortschritt', 'Fortschritt', TrendingUp]];
  const kraft = d?.kraft?.[uebung] || [];
  const maxF = Math.max(...(d?.fleiss || []).map((z) => z.einheiten), 1);
  const maxK = Math.max(...kraft.map((z) => z.e1rm), 1);
  const maxP = Math.max(...(d?.fortschritt || []).map((z) => z.prozent), 1);
  return (
    <div className="space-y-4" data-testid="coach-bestenliste">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 rounded-[12px] bg-muted p-1" role="group" aria-label="Wertung">
          {WERTUNGEN.map(([id, text]) => knopf(wertung === id, text, () => setWertung(id), `coach-wertung-${id}`))}
        </div>
        <div className="flex gap-1 rounded-[12px] bg-muted p-1" role="group" aria-label="Zeitraum">
          {[[4, '4 Wochen'], [12, '12 Wochen'], [26, '6 Monate']].map(([n, text]) => knopf(wochen === n, text, () => setWochen(n), `coach-zeitraum-${n}`))}
        </div>
      </div>
      {!d ? <div className="space-y-3" aria-busy="true"><div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-[14px]" />)}</div><Skeleton className="h-72 rounded-[14px]" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kachel label="Aktive Athleten" wert={d.gesamt.aktiv} einheit={`von ${d.gesamt.athleten}`} sub={`haben in ${d.wochen} Wochen eingetragen`} icon={Users} />
            <Kachel label="Einheiten" wert={d.gesamt.einheiten} sub={`${(d.gesamt.einheiten / d.wochen).toLocaleString('de-DE', { maximumFractionDigits: 1 })} je Woche im Schnitt`} icon={Layers} verzug={60} />
            <Kachel label="Bewegte Last" wert={tonnen(d.gesamt.tonnage)} sub="alle Sätze zusammen" icon={Dumbbell} verzug={120} />
            <Kachel label="Neue Bestwerte" wert={d.fortschritt.length} sub="Athleten mit Steigerung im Zeitraum" icon={TrendingUp} verzug={180} />
          </div>
          {wertung === 'fleiss' && (
            <Abschnitt titel="Fleiß" sub={`Einheiten seit ${datumDe(d.von)} · bei Gleichstand entscheiden Serie und bewegte Last`}>
              <Rangliste zeilen={d.fleiss} onWahl={onWahl} leer="In diesem Zeitraum hat noch niemand eingetragen."
                wert={(z) => z.einheiten} einheit="Einheiten" anteil={(z) => (z.einheiten / maxF) * 100}
                zusatz={(z) => `${String(z.je_woche).replace('.', ',')} je Woche · ${z.serie} ${z.serie === 1 ? 'Woche' : 'Wochen'} in Folge · ${tonnen(z.tonnage)}`} />
            </Abschnitt>
          )}
          {wertung === 'kraft' && (
            <Abschnitt titel="Kraft" sub="Bestes geschätztes Maximum (e1RM), ohne Zeitraum"
              rechts={d.uebungen.length > 0 && (
                <label className="shrink-0"><span className="sr-only">Übung</span>
                  <select value={uebung} onChange={(e) => setUebung(e.target.value)} data-testid="coach-uebung"
                    className="h-10 max-w-[14rem] rounded-[10px] border border-border bg-card px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {d.uebungen.map((u) => <option key={u.uebung} value={u.uebung}>{u.uebung} ({u.athleten})</option>)}
                  </select></label>)}>
              <Rangliste zeilen={kraft} onWahl={onWahl} leer="Noch keine Kraftwerte eingetragen."
                wert={(z) => Number(z.e1rm).toLocaleString('de-DE', { maximumFractionDigits: 1 })} einheit="kg" anteil={(z) => (z.e1rm / maxK) * 100}
                zusatz={(z) => `erreicht am ${datumDe(z.datum)}`} />
            </Abschnitt>
          )}
          {wertung === 'fortschritt' && (
            <Abschnitt titel="Fortschritt" sub={`Neuer Bestwert seit ${datumDe(d.von)} gegen den Bestwert davor · je Athlet die stärkste Steigerung`}>
              <Rangliste zeilen={d.fortschritt} onWahl={onWahl} leer="Im Zeitraum gab es noch keinen neuen Bestwert gegenüber früher."
                wert={(z) => `+${String(z.prozent).replace('.', ',')}`} einheit="%" anteil={(z) => (z.prozent / maxP) * 100}
                zusatz={(z) => `${z.uebung}: ${kg(z.vorher)} → ${kg(z.jetzt)}`} />
            </Abschnitt>
          )}
          <p className="text-xs text-muted-foreground">Die Bestenliste sehen nur Trainer. Sie rechnet aus den Kraftlog-Einträgen der Athleten in deinem Bereich.</p>
        </>
      )}
    </div>
  );
}

/* ── P1 Coaching: die Pakete des Coaches (Betreiber 12.10.2026) ── */

const PAKET_STATUS = { bezahlt: 'Starttermin fehlt', laufend: 'läuft', abgeschlossen: 'abgeschlossen', verfallen: 'verfallen' };

function CoachingPakete({ onAthlet, onPlan }) {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState('');
  const [werte, setWerte] = useState(null);       // { id, zeilen }
  const [busy, setBusy] = useState(false);
  const lade = useCallback(() => coach.get('/coaching').then((r) => setListe(r.data.pakete)).catch((e) => setErr(fehler(e, 'Pakete nicht geladen'))), []);
  useEffect(() => { lade(); }, [lade]);
  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!liste) return <div className="grid gap-3 sm:grid-cols-2" aria-busy="true">{[0, 1].map((i) => <Skeleton key={i} className="h-44 rounded-[14px]" />)}</div>;
  const laufend = liste.filter((p) => ['bezahlt', 'laufend'].includes(p.status));
  const speichern = async () => {
    setBusy(true);
    try { await coach.put(`/coaching/${werte.id}/testwerte`, { testwerte: werte.zeilen.filter((w) => (w.name || '').trim()) }); toast.success('Testwerte gespeichert'); setWerte(null); lade(); }
    catch (e) { toast.error(fehler(e, 'Nicht gespeichert')); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="coach-coaching">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kachel label="Laufende Pakete" wert={laufend.length} sub="bezahlt oder gestartet" icon={HeartHandshake} />
        <Kachel label="Starttermin fehlt" wert={liste.filter((p) => p.status === 'bezahlt').length} sub="bitte vereinbaren" icon={CalendarDays} verzug={60} />
        <Kachel label="Termine offen" wert={laufend.reduce((n, p) => n + (p.termine_offen || 0), 0)} sub="noch nicht eingetragen" icon={Layers} verzug={120} />
        <Kachel label="Enden in 7 Tagen" wert={laufend.filter((p) => p.tage_rest != null && p.tage_rest <= 7).length} sub="Abschluss und nächster Schritt" icon={TrendingUp} verzug={180} />
      </div>
      <CoachingCheckins laden={ccLaden} antworten={ccAntworten} />
      {!liste.length && <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Noch kein Coaching-Paket. Verkauft wird im Studio; sobald du ein Paket übernimmst, steht es hier.</CardContent></Card>}
      <div className="grid gap-4 lg:grid-cols-2">
        {liste.map((p, i) => (
          <section key={p.id} className="p1-karte border bg-card p-5 space-y-3" style={{ animationDelay: `${i * 50}ms` }} data-testid={`coach-paket-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <button type="button" className="text-left text-base font-extrabold break-words hover:underline" onClick={() => onAthlet(p.lead_id)}>{p.lead_name}</button>
                <p className="text-sm text-muted-foreground">{p.name}{p.gym_inklusive ? ' · mit Gym & Sauna' : ''}</p>
              </div>
              <Badge variant={p.status === 'laufend' ? 'default' : 'secondary'} className="text-[10px] font-bold">{PAKET_STATUS[p.status] || p.status}</Badge>
            </div>
            {p.beginn && <Balken label={`${datumDe(p.beginn)} bis ${datumDe(p.gueltig_bis)}`} hinweis={p.tage_rest != null && p.status === 'laufend' ? `noch ${Math.max(p.tage_rest, 0)} Tage` : ''}
              wert={`${p.termine_wahrgenommen} / ${p.termine_soll}`} anteil={p.laufzeit_tage ? Math.min(100, ((p.laufzeit_tage - Math.max(p.tage_rest ?? p.laufzeit_tage, 0)) / p.laufzeit_tage) * 100) : 0} />}
            {(p.termine || []).length > 0 ? (
              <ul className="space-y-1 text-sm">
                {p.termine.map((t) => (
                  <li key={t.termin_id} className="flex flex-wrap items-center justify-between gap-2">
                    <span><span className="font-semibold">{t.art === 'check' ? 'Abschluss-Check' : `Termin ${t.nr}`}</span> <span className="text-muted-foreground">{datumDe(t.am)}, {t.start}</span></span>
                    <span className="text-xs font-semibold text-muted-foreground">{t.status}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-muted-foreground">Noch kein Termin eingetragen. Termine trägt das Team im CRM ein.</p>}
            {p.garantie && <p className="text-xs text-muted-foreground">Garantie-Quote: <strong className="text-foreground">{p.garantie.ist} von {p.garantie.soll_gesamt} Einheiten ({p.garantie.quote} %)</strong></p>}
            {werte?.id === p.id ? (
              <div className="space-y-2">
                {werte.zeilen.map((w, k) => (
                  <div key={k} className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    {['name', 'start', 'zwischen', 'ende'].map((f) => (
                      <Input key={f} className="h-9" value={w[f] || ''} aria-label={`Testwert ${k + 1} ${f}`} placeholder={{ name: 'z. B. Kniebeuge', start: 'Start', zwischen: 'Woche 5', ende: 'Abschluss' }[f]}
                        onChange={(e) => setWerte({ ...werte, zeilen: werte.zeilen.map((x, j) => (j === k ? { ...x, [f]: e.target.value } : x)) })} />
                    ))}
                  </div>
                ))}
                <div className="flex gap-2"><Button size="sm" disabled={busy} onClick={speichern}>Speichern</Button><Button size="sm" variant="ghost" onClick={() => setWerte(null)}>Abbrechen</Button></div>
              </div>
            ) : (
              <>
                {(p.testwerte || []).length > 0 && (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-normal">Testwert</th><th className="font-normal">Start</th><th className="font-normal">Woche 5</th><th className="font-normal">Abschluss</th></tr></thead>
                    <tbody>{p.testwerte.map((w) => <tr key={w.name} className="border-t border-border/50"><td className="py-1 font-semibold">{w.name}</td><td>{w.start || '–'}</td><td>{w.zwischen || '–'}</td><td>{w.ende || '–'}</td></tr>)}</tbody>
                  </table>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setWerte({ id: p.id, zeilen: (p.testwerte || []).length ? p.testwerte.map((w) => ({ ...w })) : [{ name: '' }, { name: '' }, { name: '' }] })}>Testwerte eintragen</Button>
                  <Button size="sm" variant="ghost" onClick={() => onAthlet(p.lead_id)}>Kraftlog ansehen</Button>
                  {p.plan_id && <Button size="sm" variant="ghost" onClick={() => onPlan(p.plan_id)}>Plan öffnen</Button>}
                  {p.beginn && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try { const r = await coach.get(`/coaching/${p.id}/bericht`, { params: { format: 'pdf' }, responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank', 'noopener'); }
                      catch (e) { toast.error(fehler(e, 'Bericht nicht erstellt')); }
                    }}>Abschlussbericht</Button>
                  )}
                </div>
              </>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/* ── Programme ── */

function ProgrammListe({ onWahl, onEditor }) {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { coach.get('/zyklen').then((r) => setListe(r.data.zyklen)).catch((e) => setErr(fehler(e, 'Programme nicht geladen'))); }, []);
  if (err) return <p className="text-sm text-destructive" role="alert">{err}</p>;
  if (!liste) return <div className="grid gap-3 sm:grid-cols-2" aria-busy="true">{[0, 1].map((i) => <Skeleton key={i} className="h-36 rounded-[14px]" />)}</div>;
  const ki = (
    <>
      <div className="flex flex-wrap gap-2"><PlanNeu client={coach} basis={PLANER} onAngelegt={onEditor} PersonFeld={CoachPersonFeld} /></div>
      <KiPlaner statusLaden={kiStatus} bibliothekLaden={bibLaden} vorschlagen={kiVorschlag} onAngelegt={(z) => onWahl(z.id)} testId="coach-ki-planer" />
      <PlanProgramme client={coach} basis={PLANER} />
    </>
  );
  if (!liste.length) return <div className="space-y-4">{ki}<Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Keine Programme sichtbar.</CardContent></Card></div>;
  return (
    <div className="space-y-4">{ki}
    <ul className="grid gap-4 sm:grid-cols-2">
      {liste.map((z, i) => {
        const gesamt = z.wochen_anzahl || 12;
        return (
          <li key={z.id}>
            <button type="button" onClick={() => onWahl(z.id)} data-testid={`coach-zyklus-${z.id}`} style={{ animationDelay: `${i * 50}ms` }}
              className="p1-karte group flex h-full w-full flex-col rounded-[14px] border border-border bg-card px-5 py-[18px] text-left transition-colors hover:border-marke/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-[Inter] text-base font-bold break-words">{z.name}</span>
                {z.eigener_plan && <Badge variant="secondary" className="text-[10px] font-semibold">eigener Plan</Badge>}
                {!z.darf_aendern && <Badge variant="outline" className="text-[10px] font-semibold">nur lesen</Badge>}
              </span>
              <span className="mt-0.5 text-sm text-muted-foreground">{z.status}{!z.aktuelle_woche ? ` · ab ${datumDe(z.startdatum)}` : ''}</span>
              <span className="mt-auto block w-full pt-5">
                <Balken label={z.aktuelle_woche ? `Woche ${z.aktuelle_woche}` : 'Noch nicht gestartet'} hinweis={`von ${gesamt}`} wert={`${Math.round(((z.aktuelle_woche || 0) / gesamt) * 100)} %`} anteil={((z.aktuelle_woche || 0) / gesamt) * 100} />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
    </div>
  );
}

function Feld({ wert, label, onSpeichern, darf, breit, mehrzeilig }) {
  const [v, setV] = useState(wert ?? '');
  useEffect(() => { setV(wert ?? ''); }, [wert]);
  if (!darf) return <span className="break-words">{wert || '–'}</span>;
  const fertig = () => { if (String(v) !== String(wert ?? '')) onSpeichern(v); };
  const Tag = mehrzeilig ? 'textarea' : 'input';
  return (
    <Tag aria-label={label} value={v} onChange={(e) => setV(e.target.value)} onBlur={fertig}
      onKeyDown={(e) => { if (e.key === 'Enter' && !mehrzeilig) e.currentTarget.blur(); }}
      rows={mehrzeilig ? 2 : undefined}
      className={`min-h-[36px] rounded-lg border border-input bg-background px-2 py-1 text-sm ${breit ? 'w-full' : 'w-20'}`} />
  );
}

function ProgrammAnsicht({ zid, onZurueck, onEditor }) {
  const [z, setZ] = useState(null);
  const [woche, setWoche] = useState(null);
  const [err, setErr] = useState('');
  const lade = useCallback(() => coach.get(`/zyklen/${zid}`).then((r) => { setZ(r.data); setWoche((w) => w || r.data.aktuelle_woche || 1); })
    .catch((e) => setErr(fehler(e, 'Programm nicht geladen'))), [zid]);
  useEffect(() => { lade(); }, [lade]);

  const setze = async (einheit, slot, feld, wert) => {
    const werte = { [feld]: feld === 'dauer_min' ? Number(wert) : wert };
    try { const r = await coach.patch(`/zyklen/${zid}/woche`, { version: z.version, woche, einheit, slot, werte }); setZ(r.data); toast.success('Gespeichert'); }
    catch (e) {
      toast.error(fehler(e, 'Nicht gespeichert'));
      if (e?.response?.status === 409) lade();
    }
  };

  if (err) return <div className="space-y-3"><Button variant="ghost" size="sm" onClick={onZurueck}><ArrowLeft className="w-4 h-4" /> Programme</Button><p className="text-sm text-destructive" role="alert">{err}</p></div>;
  if (!z) return <Skeleton className="h-64" />;
  const w = z.wochen[woche - 1];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground" onClick={onZurueck}><ArrowLeft className="w-4 h-4" /> Programme</Button>
        {onEditor && <Button variant="outline" className="min-h-[44px] gap-1.5" onClick={() => onEditor(zid)} data-testid="coach-plan-editor"><Pencil className="w-4 h-4" /> Im Editor öffnen</Button>}
      </div>
      <div>
        <p className="text-[13px] text-muted-foreground">Training · Programme</p>
        <h2 className="text-[26px] font-extrabold tracking-tight break-words">{z.name}</h2>
        <p className="text-sm text-muted-foreground">
          {z.personen?.length ? `Für ${z.personen.map((p) => p.name).join(', ')}` : 'Kursplan'}
          {!z.darf_aendern && ' · nur lesen'}
        </p>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Woche wählen">
        {z.wochen.map((x) => (
          <button key={x.woche} type="button" role="tab" aria-selected={x.woche === woche} onClick={() => setWoche(x.woche)}
            className={`min-h-[44px] shrink-0 rounded-full border px-3.5 text-xs font-bold transition-colors ${x.woche === woche ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted hover:bg-muted/70'} ${x.woche === z.aktuelle_woche ? 'font-extrabold underline underline-offset-4' : ''}`}>
            W{x.woche}{x.typ !== 'belastung' ? ` ${x.typ === 'deload' ? 'E' : x.typ[0].toUpperCase()}` : ''}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Woche {woche} ab {datumDe(w.montag)} · {({ deload: 'Entlastungswoche', belastung: 'Belastungswoche', test: 'Testwoche', taper: 'Taper', wettkampf: 'Wettkampfwoche' })[w.typ] || w.typ}{woche === z.aktuelle_woche ? ' · läuft gerade' : ''}</p>
      {z.einheiten.map((e) => {
        const b = w.einheiten[e.key];
        return (
          <Card key={e.key}>
            <CardHeader className="pb-2"><CardTitle className="font-[Inter] text-base font-bold">Einheit {e.key} <span className="font-semibold text-muted-foreground">· {e.titel}</span></CardTitle></CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-normal">Block</th><th className="font-normal">Übung</th><th className="font-normal">Sätze</th><th className="font-normal">Wdh</th><th className="font-normal">Intensität</th><th className="font-normal">Notiz</th></tr></thead>
                <tbody>
                  {KRAFT.map((slot) => {
                    const s = b[slot] || {};
                    const uebung = slot === 'zubringer' ? (s.uebungen || []).join(', ') : s.uebung;
                    return (
                      <tr key={slot} className="border-t border-border/50 align-top">
                        <td className="py-1.5 pr-2 text-xs font-medium whitespace-nowrap">{z.labels[slot]}</td>
                        <td className="pr-2 break-words">{uebung || '–'}</td>
                        <td className="pr-2"><Feld darf={z.darf_aendern} label={`${z.labels[slot]} Sätze`} wert={s.saetze} onSpeichern={(v) => setze(e.key, slot, 'saetze', v)} /></td>
                        <td className="pr-2"><Feld darf={z.darf_aendern} label={`${z.labels[slot]} Wiederholungen`} wert={s.wdh} onSpeichern={(v) => setze(e.key, slot, 'wdh', v)} /></td>
                        <td className="pr-2">{'intensitaet' in s ? <Feld darf={z.darf_aendern} label={`${z.labels[slot]} Intensität`} wert={s.intensitaet} onSpeichern={(v) => setze(e.key, slot, 'intensitaet', v)} /> : '–'}</td>
                        <td className="min-w-[10rem]"><Feld darf={z.darf_aendern} breit label={`${z.labels[slot]} Notiz`} wert={s.notiz} onSpeichern={(v) => setze(e.key, slot, 'notiz', v)} /></td>
                      </tr>
                    );
                  })}
                  <tr className="border-t border-border/50 align-top">
                    <td className="py-1.5 pr-2 text-xs font-medium whitespace-nowrap">{z.labels.conditioning}</td>
                    <td className="pr-2" colSpan={2}>
                      {z.darf_aendern ? (
                        <select aria-label="Format" className="rounded-md border border-input bg-background px-2 py-1 text-sm" value={b.conditioning?.format || ''}
                          onChange={(ev) => setze(e.key, 'conditioning', 'format', ev.target.value)}>
                          {(z.formate || []).map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      ) : (b.conditioning?.format || '–')}
                    </td>
                    <td className="pr-2"><Feld darf={z.darf_aendern} label="Dauer in Minuten" wert={b.conditioning?.dauer_min} onSpeichern={(v) => setze(e.key, 'conditioning', 'dauer_min', v)} /></td>
                    <td colSpan={2}><Feld darf={z.darf_aendern} breit mehrzeilig label="Inhalt" wert={b.conditioning?.inhalt} onSpeichern={(v) => setze(e.key, 'conditioning', 'inhalt', v)} /></td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        );
      })}
      {z.darf_aendern && <p className="text-xs text-muted-foreground">Änderungen speichern beim Verlassen des Feldes. Übungen tauschen geht für Mainlift und Power 1 nur im Trainingsplaner für den ganzen Zyklus.</p>}
    </div>
  );
}

/* ── Seite ── */

export default function CoachPage({ start }) {
  const [ich, setIch] = useState(undefined);   // undefined = prüfe, null = abgemeldet
  const [params, setParams] = useSearchParams();
  const { bereich: pfadBereich } = useParams();       // /coach/checkins (W2.5) — die Adresse ?ansicht= geht vor
  const ansicht = params.get('ansicht') || pfadBereich || start || 'heute';   // W2: Heute ist die Startseite
  const athlet = params.get('athlet');
  const zyklus = params.get('zyklus');
  const mitglied = params.get('mitglied');
  const kunde = params.get('kunde');
  const reiter = params.get('reiter');
  const panel = params.get('panel');

  useEffect(() => {
    if (!lies()) { setIch(null); return; }
    coach.get('/ich').then((r) => setIch(r.data)).catch(() => { merke(null); setIch(null); });
  }, []);
  useEffect(() => {
    const id = coach.interceptors.response.use((r) => r, (e) => {
      if (e?.response?.status === 401 && lies()) { merke(null); setIch(null); toast.error('Bitte neu anmelden'); }
      return Promise.reject(e);
    });
    return () => coach.interceptors.response.eject(id);
  }, []);
  useEffect(() => { document.title = 'Coach-Login'; }, []);

  // Kundenakte (W1.4, 29.10.2026): alte Wege (`athlet=`, `mitglied=`) führen auf die Akte — auch als Adresse von außen
  const gehe = (p) => setParams(umleiten(p));
  useEffect(() => {
    const jetzt = Object.fromEntries(params.entries());
    const ziel = umleiten(jetzt);
    if (ziel !== jetzt) setParams(ziel, { replace: true });
  }, [params, setParams]);
  const abmelden = async () => { try { await coach.post('/abmelden'); } catch { /* egal */ } merke(null); setIch(null); setParams({}); };

  const [dunkel, setDunkel] = useCoachStil();
  const [q, setQ] = useState('');
  const [untertitel, setUntertitel] = useState('');   // Satz unter dem Gruß auf Heute, liefert CoachHeute
  const mitErnaehrung = !!(ich && (ich.ernaehrung || ich.master));
  const zaehler = useCockpitZaehler(mitErnaehrung);
  const inbox = useInboxZaehler(!!ich, ansicht);

  if (ich === undefined) return <main className="min-h-screen bg-background p-6"><Skeleton className="mx-auto h-40 max-w-md rounded-[14px]" /></main>;
  if (!ich) return <Anmeldung onDrin={setIch} />;

  /* Ernährungscoaching komplett auf /coach (15.10.2026): eigene Gruppe mit Start, Cockpit, Kunden, Automatik, Kochbüchern
     und (nur Master) Einstellungen — nur mit Schalter „Ernährung“ am Zugang oder als Master. Rollen prüft der Server. */
  const stunde = new Date().getHours();
  const gruss = `${stunde < 11 ? 'Guten Morgen' : stunde < 18 ? 'Hallo' : 'Guten Abend'}, ${(ich.name || '').split(/\s+/)[0] || 'Coach'}`;
  const bereiche = [
    { gruppe: 'Coach', id: 'heute', label: 'Heute', icon: Home, titel: gruss, oben: new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' }), sub: untertitel },
    { gruppe: 'Coach', id: 'checkins', label: 'Check-ins', icon: ClipboardCheck, titel: 'Check-ins', sub: 'Inbox, Auswertung und Archiv – Ernährungscoaching und P1 Coaching zusammen', zaehler: inbox?.checkins },
    { gruppe: 'Coach', id: 'fortschritt', label: 'Fortschritt', icon: BarChart3, titel: 'Fortschritt', sub: 'Portfolio: Momentum, Status und Entwicklung aller Kunden' },
    { gruppe: 'Athleten', id: 'athleten', label: 'Athleten / Kundenakte', icon: Users, titel: 'Athleten', sub: 'Kraftlogs, Bestwerte und Rückmeldungen' },
    { gruppe: 'Athleten', id: 'bestenliste', label: 'Bestenliste', icon: Trophy, titel: 'Bestenliste', sub: 'Fleiß, Kraft und Fortschritt aller Athleten im Vergleich' },
    { gruppe: 'Athleten', id: 'coaching', label: 'P1 Coaching', icon: HeartHandshake, titel: 'P1 Coaching', sub: 'Deine Pakete: Termine, Laufzeit und Testwerte' },
    { gruppe: 'Training', id: 'programme', label: 'Programme', icon: Layers, titel: 'Programme', sub: 'Zyklen Woche für Woche ansehen und anpassen' },
    { gruppe: 'Training', id: 'uebungen', label: 'Übungen', icon: BookOpen, titel: 'Übungsbibliothek', sub: 'Anleitung und Video je Übung – die App zeigt beides zur geplanten Übung' },
    ...(mitErnaehrung ? [
      { gruppe: 'Ernährung', id: 'ernaehrung', label: 'Start', icon: Salad, titel: 'Ernährung', sub: 'Dashboard: Ampeln, To-dos und deine Kunden im Ernährungscoaching' },
      { gruppe: 'Ernährung', id: 'e-cockpit', label: 'Cockpit', icon: LayoutDashboard, titel: 'Coaching-Cockpit', sub: 'Kundenliste, Konversation und Kundenakte auf einen Blick', zaehler: zaehler?.cockpit },
      { gruppe: 'Ernährung', id: 'e-kunden', label: 'Kunden', icon: UserRound, titel: 'Kunden', sub: 'Übersicht, Entwicklung, Protokoll, Plan und Formulare je Kunde', zaehler: zaehler?.kunden },
      { gruppe: 'Ernährung', id: 'e-automatik', label: 'Automatik', icon: Workflow, titel: 'Automatik', sub: 'Sequenzen und Läufe – Schritt für Schritt nach dem Auslöser' },
      { gruppe: 'Ernährung', id: 'e-kochbuecher', label: 'Kochbücher', icon: BookOpenCheck, titel: 'Kochbücher', sub: 'Rezeptbibliothek, Abschnitte und Sichtbarkeit in der App' },
      ...(ich.master ? [{ gruppe: 'Ernährung', id: 'e-einstellungen', label: 'Einstellungen', icon: Settings2, titel: 'Einstellungen', sub: 'Abo-Produkt, Einwilligung, Kurs-Tipps, Risiko, Garmin, Tresor für alle' }] : []),
    ] : []),
    { gruppe: 'Community & Verkauf', id: 'nachrichten', label: 'Nachrichten & Feed', icon: MessageSquare, titel: 'Nachrichten', sub: 'Alle Coach-Chats deiner Kunden, ungelesene zuerst', zaehler: inbox?.nachrichten },
    { gruppe: 'Community & Verkauf', id: 'challenges', label: 'Challenges', icon: Footprints, titel: 'Team-Challenges', sub: 'Schritte-Challenge für ein Team aus deinen Kunden' },
    { gruppe: 'Community & Verkauf', id: 'kalender', label: 'Kalender & Angebote', icon: CalendarDays, titel: 'Kalender', sub: 'PT, Check-ups, Gruppen und Blocker – Kurse als Einsatz ausgegraut' },
    ...((ich.master || ich.rolle === 'overviewer') ? [{ gruppe: 'Community & Verkauf', id: 'testimonials', label: 'Testimonials', icon: TestimonialSymbol, titel: 'Testimonial-Bibliothek', sub: 'Freigegebene Ergebnisse – Zahlen und Fotos ohne Gesicht, nie ein Name' }] : []),
  ];
  const akteOffen = ansicht === 'kunde' && !!kunde;
  const aktiv = akteOffen
    ? { id: 'kunde', titel: 'Kundenakte', sub: 'Training, Ernährung, Check-ins und Angebote auf einer Seite' }
    : bereiche.find((b) => b.id === ansicht) || bereiche[0];
  const liste = (aktiv.id === 'athleten' && !athlet) || (aktiv.id === 'e-kunden' && !mitglied);
  const themaKnopf = (
    <button type="button" onClick={() => setDunkel((v) => !v)} aria-label={dunkel ? 'Helles Thema' : 'Dunkles Thema'} data-testid="coach-thema"
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-border bg-card text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {dunkel ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
  const punkt = (b, kurzform) => (
    <button key={b.id} type="button" onClick={() => gehe({ ansicht: b.id })} aria-current={aktiv.id === b.id ? 'page' : undefined} data-testid={`coach-nav-${b.id}`}
      className={`flex items-center gap-3 rounded-[8px] px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${kurzform ? 'shrink-0 py-2.5 font-semibold' : 'w-full py-[9px] text-left'} ${aktiv.id === b.id ? 'bg-accent font-bold text-foreground' : 'coach-punkt hover:bg-muted/60 hover:text-foreground'}`}>
      {kurzform && <b.icon className={`h-4 w-4 ${aktiv.id === b.id ? 'text-marke' : ''}`} aria-hidden="true" />} {b.label}
      {b.zaehler > 0 && <span className={`${kurzform ? '' : 'ml-auto'} rounded-full bg-marke px-1.5 text-[10px] font-bold text-white`} aria-label={`${b.zaehler} offen`} data-testid={`coach-zaehler-${b.id}`}>{b.zaehler}</span>}
    </button>
  );
  const logo = (h) => (<>
    <img src="/logo-weiss-kompakt.png" alt="P1 Performance Center" className={`${h} hidden w-auto object-contain dark:block`} />
    <img src="/logo-black-kompakt.png" alt="P1 Performance Center" className={`${h} w-auto object-contain dark:hidden`} />
  </>);

  return (
    <div className="min-h-screen bg-background text-foreground lg:grid lg:grid-cols-[240px_1fr]">
      {/* Seitenleiste (ab Desktop) */}
      <aside className="coach-leiste sticky top-0 hidden h-screen flex-col border-r border-border px-3.5 py-[22px] lg:flex">
        <div className="flex items-center gap-2.5 px-2.5 pb-2">{logo('h-8')}<span className="text-xs font-extrabold uppercase tracking-[0.14em] text-foreground/80">Coaching</span></div>
        <nav className="mt-2 flex-1 space-y-1 overflow-y-auto" aria-label="Bereiche">
          {bereiche.map((b, i) => (
            <div key={b.id} className={i && bereiche[i - 1].gruppe === b.gruppe ? '!mt-0.5' : ''}>
              {(!i || bereiche[i - 1].gruppe !== b.gruppe) && <p className="mb-1.5 px-3 pt-2 text-[11px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">{b.gruppe}</p>}
              {punkt(b)}
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-3 rounded-[10px] px-2 py-2">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-extrabold" aria-hidden="true">{kuerzel(ich.name)}</span>
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold">{ich.name}</span><span className="block truncate text-xs text-muted-foreground">{ich.rolle_text}</span></span>
          <button type="button" onClick={abmelden} aria-label="Abmelden" title="Abmelden" data-testid="coach-abmelden"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>

      <div className="min-w-0">
        {/* Leiste oben (Handy und Tablet) */}
        <div className="flex items-center gap-2 border-b border-border/70 px-4 py-3 lg:hidden">
          {logo('h-8')}
          <nav className="flex min-w-0 flex-1 gap-1 overflow-x-auto" aria-label="Bereiche">{bereiche.map((b) => punkt(b, true))}</nav>
          <button type="button" onClick={abmelden} aria-label="Abmelden" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-muted-foreground hover:bg-muted"><LogOut className="h-4 w-4" /></button>
        </div>

        <main className="w-full max-w-[1240px] px-4 py-6 sm:px-[30px] lg:pb-12 lg:pt-[26px]" data-testid="coach-seite">
          <header className="mb-[18px] flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[13px] text-muted-foreground">{aktiv.oben || (aktiv.gruppe ? `${aktiv.gruppe} · ${aktiv.label}` : 'Coach')}</p>
              <h1 className="text-[28px] font-extrabold leading-tight sm:text-[30px]">{aktiv.titel}</h1>
              {aktiv.sub && <p className="mt-1 text-[15px] text-muted-foreground">{aktiv.sub}</p>}
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              {liste && (
                <label className="relative block flex-1 sm:w-72 sm:flex-none">
                  <span className="sr-only">{aktiv.id === 'e-kunden' ? 'Kunden suchen' : 'Athleten suchen'}</span>
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name suchen" data-testid="coach-suche"
                    className="h-11 w-full rounded-[10px] border border-border bg-card pl-10 pr-3 text-sm font-medium placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </label>
              )}
              {!liste && <CoachSuche coach={coach} bereiche={bereiche} gehe={gehe} />}
              <span>{themaKnopf}</span>
            </div>
          </header>
          {akteOffen
            ? <CoachKundeAkte key={kunde} coach={coach} ich={ich} leadId={kunde} reiter={reiter} ereiter={params.get('ereiter')} checkin={params.get('checkin')} stand={params.get('stand')} gehe={gehe} Training={AthletAnsicht} />
            : aktiv.id === 'heute' ? <CoachHeute coach={coach} gehe={gehe} setUntertitel={setUntertitel} />
            : aktiv.id === 'fortschritt' ? <CoachPortfolio coach={coach} gehe={gehe} />
            : aktiv.id === 'nachrichten' ? <CoachNachrichten coach={coach} gehe={gehe} kunde={kunde} />
            : istErnaehrung(aktiv.id)
            ? <CoachErnaehrungBereich coach={coach} ich={ich} ansicht={aktiv.id} params={{ mitglied, kunde, reiter, panel }} gehe={gehe} q={q} />
            : aktiv.id === 'checkins' ? <CoachCheckins coach={coach} checkin={params.get('checkin')} stand={params.get('stand')} gehe={gehe} />
            : aktiv.id === 'kalender' ? <CoachKalender coach={coach} ich={ich} kunde={kunde} gehe={gehe} />
            : aktiv.id === 'coaching' ? <CoachingPakete onAthlet={(id) => gehe({ ansicht: 'athleten', athlet: id })} onPlan={(id) => gehe({ ansicht: 'programme', zyklus: id })} />
            : aktiv.id === 'uebungen' ? <Uebungsbibliothek laden={bibLaden} speichern={bibSpeichern} />
            : aktiv.id === 'testimonials' ? <TestimonialBibliothek client={ich.master ? tmBibMaster : tmBib} />
            : aktiv.id === 'challenges' ? <CoachTeamChallenges client={coach} />
            : aktiv.id === 'bestenliste' ? <Bestenliste onWahl={(id) => gehe({ ansicht: 'athleten', athlet: id })} />
            : aktiv.id === 'programme'
            ? (zyklus && params.get('editor') ? <TrainingsplanEditor client={coach} basis={PLANER} zid={zyklus} onGeoeffnet={(id) => gehe({ ansicht: 'programme', zyklus: id, editor: '1' })} onZurueck={() => gehe({ ansicht: 'programme', zyklus })} />
              : zyklus ? <ProgrammAnsicht zid={zyklus} onZurueck={() => gehe({ ansicht: 'programme' })} onEditor={(id) => gehe({ ansicht: 'programme', zyklus: id, editor: '1' })} />
              : <ProgrammListe onWahl={(id) => gehe({ ansicht: 'programme', zyklus: id })} onEditor={(id) => gehe({ ansicht: 'programme', zyklus: id, editor: '1' })} />)
            : (athlet ? <AthletAnsicht mid={athlet} gehe={gehe} onZurueck={() => gehe({ ansicht: 'athleten' })} onPlan={(id) => gehe({ ansicht: 'programme', zyklus: id, editor: '1' })} /> : <AthletenListe q={q} onWahl={(id) => gehe({ ansicht: 'athleten', athlet: id })} />)}
        </main>
      </div>
    </div>
  );
}
